// Helpers for building the note-save payload
// Background (#720): the review modal can open before async pipelines (patient summary, note, macros, codes) finish generating; the front end loads those fields as empty/stale, so a note-only save would clobber the freshly generated data on the server; to prevent this the client snapshots the values it loaded and, on save, sends null for any generated field the user did not edit, and the server preserves those columns via COALESCE($n, column) so a null leaves the existing value intact

export interface NoteSnapshot {
  note?: unknown;
  patientSummary?: unknown;
  encounterTime?: unknown;
  macros?: unknown;
  selectedCodes?: unknown;
}

export interface NoteValues {
  note: unknown;
  patientSummary: unknown;
  LOSCode: unknown;
  codingInfo: unknown;
  encounterTime: unknown;
  timeBasedCode: unknown;
  icd_10_codes: unknown;
  orders_v2: unknown;
  hcc_codes: unknown;
  cpt_codes: unknown;
  mdmSelectedObj: unknown;
  macros: unknown;
  userFeedback: unknown;
}

// Check whether the user actually changed this field's value
export const isUnchangedFromSnapshot = (current: unknown, snapshot: unknown): boolean => {
  if (current === snapshot) return true;
  if (current == null || snapshot == null) return false;
  if (typeof current === "object" && typeof snapshot === "object") {
    try {
      return JSON.stringify(current) === JSON.stringify(snapshot);
    } catch {
      return false;
    }
  }
  return false;
};

// Returns the value if edited, else null to leave it untouched; preserved server-side via COALESCE
export const editedValueOrIgnore = <T>(current: T, snapshot: unknown): T | null =>
  isUnchangedFromSnapshot(current, snapshot) ? null : current;

// Builds the request body for PUT /notes/todos/:id; generated fields (note, patientSummary, encounterTime, macros) are sent only when edited, otherwise they are null so the server keeps the existing value; everything else is passed through unchanged
export const buildNotePayload = (values: NoteValues, snapshot: NoteSnapshot = {}) => {
  const {
    note,
    patientSummary,
    LOSCode,
    codingInfo,
    encounterTime,
    timeBasedCode,
    icd_10_codes,
    orders_v2,
    hcc_codes,
    cpt_codes,
    mdmSelectedObj,
    macros,
    userFeedback,
  } = values;

  return {
    note: editedValueOrIgnore(note, snapshot.note),
    patientSummary: editedValueOrIgnore(patientSummary, snapshot.patientSummary),
    LOSCode,
    codingInfo,
    encounterTime: editedValueOrIgnore(encounterTime, snapshot.encounterTime),
    timeBasedCode,
    icd_10_codes,
    orders_v2,
    hcc_codes,
    cpt_codes,
    mdmSelectedObj,
    macros: editedValueOrIgnore(macros, snapshot.macros),
    userFeedback,
  };
};
